import axios from "axios";
import React, { useEffect, useState } from "react";
import { env } from "../config";
import { useDispatch, useSelector } from "react-redux";
import * as Actions from "../redux/actions";
import { useLocation, useNavigate, useParams } from "react-router";
import Loader from "../components/Loader";
import Footer from "../components/Footer";

function WikipeedikaIndividualBlogPage() {
  const { key } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const WikipeedikaPageData: any = useSelector(
    (state: any) => state?.Page?.WikipeedikaPageContent
  );

  const WikipeedikaBlogData: any[] = useSelector(
    (state: any) => state?.Page?.WikipeedikaBlogContent
  ) || [];

  // Converts both CMS titles and URL values into the same format.
  //
  // Example:
  // "Building for Failure : How Databases Survive the Unexpected"
  //
  // becomes:
  // "building-for-failure-how-databases-survive-the-unexpected"
  const createSlug = (title: string = "") => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
  };

  const convertDateFormat = (inputDate: string) => {
    const dateParts = inputDate.split("/");

    const [day, month, year] = dateParts.map((part) =>
      parseInt(part, 10)
    );

    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    return `${months[month - 1]} ${day}, ${year}`;
  };

  /*
   * Fetch page-level content.
   */
  useEffect(() => {
    const fetchPageContent = async () => {
      // Already loaded in Redux.
      if (Object.keys(WikipeedikaPageData || {}).length > 0) {
        return;
      }

      try {
        const response = await axios.get(
          `${env}/api/wikipeedika-page-content?populate=deep`
        );

        dispatch(
          Actions.WikipeedikapageAction(
            response?.data?.data?.attributes
          )
        );
      } catch (error) {
        console.error(
          "Failed to fetch Wikipeedika page content:",
          error
        );
      }
    };

    fetchPageContent();
  }, [dispatch, WikipeedikaPageData]);

  /*
   * Fetch blogs.
   *
   * On a hard refresh Redux starts empty, so this request
   * repopulates WikipeedikaBlogData.
   */
  useEffect(() => {
    const fetchBlogs = async () => {
      // Already loaded in Redux.
      if (WikipeedikaBlogData.length > 0) {
        return;
      }

      try {
        const response = await axios.get(
          `${env}/api/wikipeedika-blogs?populate=deep&pagination[page]=1&pagination[pageSize]=99`
        );

        const blogs = response?.data?.data || [];

        console.log("Fetched Wikipeedika blogs:", blogs.length);

        dispatch(
          Actions.WikipeedikaBlogContentAction(blogs)
        );
      } catch (error) {
        console.error(
          "Failed to fetch Wikipeedika blogs:",
          error
        );
      }
    };

    fetchBlogs();
  }, [dispatch, WikipeedikaBlogData.length]);

  /*
   * Scroll to the top whenever the user opens another article.
   */
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, [key]);

  /*
   * currentPage is derived data.
   *
   * We don't need useState/useEffect for it.
   * Whenever Redux or `key` changes, the component renders
   * again and calculates the correct index.
   */
  const currentPage = (() => {
    if (!key || WikipeedikaBlogData.length === 0) {
      return -1;
    }

    /*
     * Handles both:
     *
     * building-for-failure-how-databases...
     *
     * and old URLs such as:
     *
     * building-for-failure-:-how-databases...
     */
    const targetSlug = createSlug(
      decodeURIComponent(key).replace(/-/g, " ")
    );

    return WikipeedikaBlogData.findIndex((node: any) => {
      const blogSlug = createSlug(
        node?.attributes?.Title || ""
      );

      return blogSlug === targetSlug;
    });
  })();

  const handleSideBarClick = (title: string) => {
    navigate(`/wikipeedika/${createSlug(title)}`);
  };

  /*
   * IMPORTANT:
   *
   * Check loading BEFORE checking currentPage === -1.
   *
   * On refresh:
   *
   * WikipeedikaBlogData = []
   * currentPage = -1
   *
   * That does NOT mean "Blog not found".
   * It means we're still waiting for the API.
   */
  const isLoading = WikipeedikaBlogData.length === 0;

  if (isLoading) {
    return <Loader />;
  }

  /*
   * Only show "not found" AFTER the API data exists.
   */
  if (currentPage === -1) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        Blog not found
      </div>
    );
  }

  /*
   * Store the selected blog so we don't repeatedly write:
   *
   * WikipeedikaBlogData[currentPage]?.attributes
   */
  const currentBlog =
    WikipeedikaBlogData[currentPage]?.attributes;


  // const isLoading = Object.keys(WikipeedikaBlogData)?.length === 0;


  if (currentPage === -1) {
    return <div>Blog not found</div>;
  }
  return isLoading ? (
    <Loader />
  ) : (
    <>
      <div
        style={{
          backgroundImage: `url(${env}${WikipeedikaPageData?.WikipeedikaIntro?.backgroundImage?.data?.attributes?.url})`,
        }}
        className={`font-custom bg-no-repeat bg-cover text-black-75  h-max   px-5 py-7 lg:px-10 xl:px-20 lg:py-14 bg-center flex flex-col  `}
      >
        <div className="md:grid  md:grid-cols-[0.50fr_1fr_1fr] gap-5 lg:mx-auto lg:max-w-screen-2xl">
          <button
            onClick={() => navigate("/wikipeedika")}
            data-aos="zoom-in"
            className="p-2  hidden md:flex items-center justify-center orde"
          >
            <svg
              className="w-full h-auto"
              width="570"
              height="143"
              viewBox="0 0 570 143"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M40.15 50.55L28.3 104.85H15.4L0.1 -7.15256e-06H7.9L21.7 95.25H22.9L35.05 40.95H46.45L58.45 95.25H59.65L73.45 -7.15256e-06H81.25L65.95 104.85H53.05L41.35 50.55H40.15ZM96.0063 -7.15256e-06H103.956V11.7H96.0063V-7.15256e-06ZM96.0063 30H103.956V105H96.0063V30ZM151.756 105L135.706 69.15H130.756V105H123.106V-7.15256e-06H130.756V60H135.556L151.606 30.15H160.156L141.706 64.8L160.306 105H151.756ZM173.204 -7.15256e-06H181.154V11.7H173.204V-7.15256e-06ZM173.204 30H181.154V105H173.204V30ZM208.103 93.6C209.303 94.4 210.703 95.1 212.303 95.7C213.903 96.3 215.503 96.85 217.103 97.35C218.703 97.85 220.203 98.25 221.603 98.55C223.103 98.75 224.303 98.85 225.203 98.85C228.703 98.85 231.653 97.6 234.053 95.1C236.453 92.5 237.653 89.4 237.653 85.8V49.05C237.653 47.25 237.303 45.6 236.603 44.1C236.003 42.5 235.153 41.15 234.053 40.05C232.953 38.85 231.653 37.9 230.153 37.2C228.653 36.5 227.053 36.15 225.353 36.15C224.453 36.15 223.303 36.25 221.903 36.45C220.503 36.55 219.003 36.85 217.403 37.35C215.903 37.75 214.353 38.3 212.753 39C211.153 39.7 209.603 40.55 208.103 41.55V93.6ZM207.953 102.3V142.5H200.453V37.05L197.153 33.9L202.403 28.5L207.053 33C210.453 31.3 213.753 30.15 216.953 29.55C220.153 28.85 222.903 28.5 225.203 28.5C228.003 28.5 230.603 29.05 233.003 30.15C235.403 31.25 237.503 32.75 239.303 34.65C241.103 36.55 242.503 38.8 243.503 41.4C244.603 44 245.153 46.75 245.153 49.65V85.35C245.153 88.25 244.603 91 243.503 93.6C242.503 96.1 241.103 98.35 239.303 100.35C237.503 102.25 235.403 103.75 233.003 104.85C230.603 105.95 228.003 106.5 225.203 106.5C223.503 106.5 221.203 106.3 218.303 105.9C215.403 105.6 211.953 104.4 207.953 102.3ZM287.295 68.85H287.595C289.395 68.85 291.045 68.5 292.545 67.8C294.045 67 295.345 66 296.445 64.8C297.545 63.5 298.395 62.05 298.995 60.45C299.695 58.85 300.045 57.15 300.045 55.35V49.05C300.045 46.65 299.595 44.65 298.695 43.05C297.795 41.35 296.595 40 295.095 39C293.695 38 291.995 37.3 289.995 36.9C288.095 36.4 286.095 36.15 283.995 36.15C282.095 36.15 280.245 36.45 278.445 37.05C276.745 37.65 275.245 38.5 273.945 39.6C272.645 40.7 271.595 42.05 270.795 43.65C269.995 45.25 269.595 47.05 269.595 49.05V68.7L287.295 68.85ZM261.795 49.8C261.795 46.8 262.295 44 263.295 41.4C264.395 38.8 265.845 36.55 267.645 34.65C269.545 32.75 271.695 31.25 274.095 30.15C276.595 29.05 279.295 28.5 282.195 28.5H287.295C290.095 28.5 292.745 29.05 295.245 30.15C297.745 31.25 299.945 32.75 301.845 34.65C303.745 36.55 305.195 38.8 306.195 41.4C307.295 44 307.845 46.8 307.845 49.8V54.75C307.845 58.95 307.095 62.5 305.595 65.4C304.095 68.2 302.045 70.5 299.445 72.3C296.845 74.1 293.795 75.4 290.295 76.2C286.895 76.9 283.245 77.25 279.345 77.25C277.745 77.25 276.145 77.2 274.545 77.1C272.945 77 271.295 76.85 269.595 76.65V85.65C270.295 89.65 272.145 92.85 275.145 95.25C278.245 97.55 281.745 98.7 285.645 98.7C286.045 98.7 287.295 98.5 289.395 98.1C291.595 97.6 293.895 97.1 296.295 96.6C299.095 96 302.195 95.3 305.595 94.5L307.395 102.45C303.695 103.25 300.345 103.9 297.345 104.4C294.745 104.9 292.245 105.35 289.845 105.75C287.545 106.15 286.145 106.35 285.645 106.35C282.445 106.35 279.395 105.8 276.495 104.7C273.595 103.5 271.045 101.85 268.845 99.75C266.645 97.65 264.895 95.2 263.595 92.4C262.395 89.5 261.795 86.35 261.795 82.95V49.8ZM348.379 68.85H348.679C350.479 68.85 352.129 68.5 353.629 67.8C355.129 67 356.429 66 357.529 64.8C358.629 63.5 359.479 62.05 360.079 60.45C360.779 58.85 361.129 57.15 361.129 55.35V49.05C361.129 46.65 360.679 44.65 359.779 43.05C358.879 41.35 357.679 40 356.179 39C354.779 38 353.079 37.3 351.079 36.9C349.179 36.4 347.179 36.15 345.079 36.15C343.179 36.15 341.329 36.45 339.529 37.05C337.829 37.65 336.329 38.5 335.029 39.6C333.729 40.7 332.679 42.05 331.879 43.65C331.079 45.25 330.679 47.05 330.679 49.05V68.7L348.379 68.85ZM322.879 49.8C322.879 46.8 323.379 44 324.379 41.4C325.479 38.8 326.929 36.55 328.729 34.65C330.629 32.75 332.779 31.25 335.179 30.15C337.679 29.05 340.379 28.5 343.279 28.5H348.379C351.179 28.5 353.829 29.05 356.329 30.15C358.829 31.25 361.029 32.75 362.929 34.65C364.829 36.55 366.279 38.8 367.279 41.4C368.379 44 368.929 46.8 368.929 49.8V54.75C368.929 58.95 368.179 62.5 366.679 65.4C365.179 68.2 363.129 70.5 360.529 72.3C357.929 74.1 354.879 75.4 351.379 76.2C347.979 76.9 344.329 77.25 340.429 77.25C338.829 77.25 337.229 77.2 335.629 77.1C334.029 77 332.379 76.85 330.679 76.65V85.65C331.379 89.65 333.229 92.85 336.229 95.25C339.329 97.55 342.829 98.7 346.729 98.7C347.129 98.7 348.379 98.5 350.479 98.1C352.679 97.6 354.979 97.1 357.379 96.6C360.179 96 363.279 95.3 366.679 94.5L368.479 102.45C364.779 103.25 361.429 103.9 358.429 104.4C355.829 104.9 353.329 105.35 350.929 105.75C348.629 106.15 347.229 106.35 346.729 106.35C343.529 106.35 340.479 105.8 337.579 104.7C334.679 103.5 332.129 101.85 329.929 99.75C327.729 97.65 325.979 95.2 324.679 92.4C323.479 89.5 322.879 86.35 322.879 82.95V49.8ZM421.163 93.3V41.55C417.663 39.25 414.363 37.8 411.263 37.2C408.163 36.6 405.763 36.3 404.063 36.3C400.563 36.3 397.613 37.6 395.213 40.2C392.813 42.7 391.613 45.75 391.613 49.35V85.8C391.613 87.6 391.913 89.3 392.513 90.9C393.213 92.4 394.113 93.75 395.213 94.95C396.313 96.05 397.613 96.95 399.113 97.65C400.613 98.35 402.213 98.7 403.913 98.7C404.813 98.7 406.163 98.6 407.963 98.4C409.763 98.1 411.563 97.75 413.363 97.35C415.263 96.85 416.963 96.3 418.463 95.7C419.963 95 420.863 94.2 421.163 93.3ZM421.313 32.85V0.149991H428.813V97.8L432.113 101.1L426.713 106.35L422.213 101.85C419.013 103.65 415.713 104.85 412.313 105.45C408.913 106.05 406.163 106.35 404.063 106.35C401.263 106.35 398.663 105.8 396.263 104.7C393.863 103.6 391.763 102.1 389.963 100.2C388.163 98.3 386.713 96.05 385.613 93.45C384.613 90.85 384.113 88.1 384.113 85.2V49.8C384.113 46.9 384.613 44.2 385.613 41.7C386.713 39.1 388.163 36.85 389.963 34.95C391.763 32.95 393.863 31.4 396.263 30.3C398.663 29.2 401.263 28.65 404.063 28.65C405.763 28.65 408.063 28.85 410.963 29.25C413.863 29.55 417.313 30.75 421.313 32.85ZM448.155 -7.15256e-06H456.105V11.7H448.155V-7.15256e-06ZM448.155 30H456.105V105H448.155V30ZM503.904 105L487.854 69.15H482.904V105H475.254V-7.15256e-06H482.904V60H487.704L503.754 30.15H512.304L493.854 64.8L512.454 105H503.904ZM559.412 68.55C559.412 68.45 558.963 68.15 558.063 67.65C557.163 67.05 555.913 66.45 554.313 65.85C552.813 65.15 551.013 64.55 548.913 64.05C546.813 63.45 544.613 63.15 542.313 63.15C540.613 63.15 538.963 63.5 537.362 64.2C535.862 64.9 534.563 65.85 533.463 67.05C532.363 68.25 531.463 69.65 530.763 71.25C530.163 72.75 529.863 74.4 529.863 76.2V85.65C529.863 87.45 530.163 89.15 530.763 90.75C531.463 92.25 532.363 93.6 533.463 94.8C534.563 95.9 535.862 96.8 537.362 97.5C538.863 98.2 540.463 98.55 542.163 98.55C543.663 98.55 545.363 98.3 547.263 97.8C549.263 97.2 551.163 96.55 552.963 95.85C555.063 95.05 557.213 94.15 559.412 93.15V68.55ZM567.063 97.65L569.913 100.35L564.513 105.75L560.463 101.7C557.263 103.5 553.913 104.75 550.413 105.45C547.013 106.15 544.313 106.5 542.313 106.5C539.513 106.5 536.863 105.95 534.363 104.85C531.963 103.65 529.863 102.1 528.063 100.2C526.263 98.2 524.863 95.9 523.863 93.3C522.863 90.7 522.363 87.95 522.363 85.05V76.65C522.363 73.75 522.863 71.05 523.863 68.55C524.863 65.95 526.263 63.7 528.063 61.8C529.863 59.9 531.963 58.4 534.363 57.3C536.863 56.2 539.513 55.65 542.313 55.65C543.013 55.65 544.063 55.7 545.463 55.8C546.963 55.9 548.513 56.1 550.113 56.4C551.813 56.7 553.513 57.15 555.213 57.75C556.913 58.25 558.363 58.95 559.563 59.85V54.75C559.563 52.45 559.113 50.2 558.213 48C557.413 45.7 556.313 43.65 554.912 41.85C553.512 40.05 551.863 38.6 549.963 37.5C548.063 36.4 546.013 35.85 543.813 35.85C543.013 35.85 541.563 36.1 539.463 36.6C537.463 37.1 535.363 37.6 533.163 38.1C530.563 38.7 527.763 39.4 524.763 40.2L522.663 33.15C525.963 32.35 529.013 31.6 531.813 30.9C534.213 30.3 536.563 29.75 538.863 29.25C541.163 28.75 542.813 28.5 543.813 28.5C547.013 28.5 550.013 29.25 552.812 30.75C555.613 32.25 558.063 34.25 560.163 36.75C562.263 39.15 563.913 41.95 565.113 45.15C566.313 48.25 566.913 51.45 566.913 54.75L567.063 97.65Z"
                fill="black"
              />
            </svg>
          </button>

          <div className="bg-white  rounded-md shadow-md col-span-2 col-start-2 p-5 md:p-10">
            <div className="text-left  font-custom font-bold !leading-[1.2em] text-3xl md:text-4xl  ">
              {WikipeedikaBlogData[currentPage]?.attributes?.Title}
            </div>
            <div className="text-black-75 md:text-xl my-4 font-custom  md:flex hidden  gap-5  items-center ">
              <span className="text-[#B10B0B] font-bold ">
                {WikipeedikaBlogData[currentPage]?.attributes?.Presenter}
              </span>
              <span className="">|</span>
              <span className="text-xl">
                {WikipeedikaBlogData[currentPage]?.attributes?.Date &&
                  convertDateFormat(
                    WikipeedikaBlogData[currentPage]?.attributes?.Date
                  )}
              </span>
            </div>
            <div className="text-black-75 md:text-xl my-4 font-custom  flex flex-col md:hidden gap-2  items-start ">
              <div className="text-[#B10B0B] font-bold ">
                {WikipeedikaBlogData[currentPage]?.attributes?.Presenter}
              </div>
              <div className="text-xl">
                {WikipeedikaBlogData[currentPage]?.attributes?.Date &&
                  convertDateFormat(
                    WikipeedikaBlogData[currentPage]?.attributes?.Date
                  )}
              </div>
            </div>
          </div>
          <div className="col-start-1  hidden md:block h-max bg-white bg-opacity-20 p-4 shadow-md rounded-md">
            <div className="font-custom font-bold text-black-75  w-full text-base ">
              Recent Updates
            </div>
            {[...WikipeedikaBlogData]?.sort((a: any, b: any) => {
              a = a?.attributes?.Date.split("/").reverse().join("");
              b = b?.attributes?.Date.split("/").reverse().join("");
              return b > a ? 1 : b < a ? -1 : 0;
            })
              .filter((node: any, index: number) => index < 5)
              .map((node: any, index: number) => {
                return (
                  <div className="w-full bg-white rounded-md shadow-md  text-black-75 text-sm my-4 p-4 border-l-4 border-[#B10B0B]">
                    <div className="flex items-center gap-2">
                      <svg
                        width="14"
                        height="13"
                        viewBox="0 0 14 13"
                        className="stroke-[#8A8A8A] shrink-0"
                        fill="#8A8A8A"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M9.31699 9.51669L10.017 8.81669L7.50033 6.30002V3.16669H6.50033V6.70002L9.31699 9.51669ZM7.00033 12.8334C6.12255 12.8334 5.29766 12.6667 4.52566 12.3334C3.75321 12 3.08366 11.55 2.51699 10.9834C1.95033 10.4167 1.50033 9.74713 1.16699 8.97469C0.833659 8.20269 0.666992 7.3778 0.666992 6.50002C0.666992 5.62224 0.833659 4.79713 1.16699 4.02469C1.50033 3.25269 1.95033 2.58335 2.51699 2.01669C3.08366 1.45002 3.75321 1.00002 4.52566 0.666687C5.29766 0.333354 6.12255 0.166687 7.00033 0.166687C7.8781 0.166687 8.70321 0.333354 9.47566 0.666687C10.2477 1.00002 10.917 1.45002 11.4837 2.01669C12.0503 2.58335 12.5003 3.25269 12.8337 4.02469C13.167 4.79713 13.3337 5.62224 13.3337 6.50002C13.3337 7.3778 13.167 8.20269 12.8337 8.97469C12.5003 9.74713 12.0503 10.4167 11.4837 10.9834C10.917 11.55 10.2477 12 9.47566 12.3334C8.70321 12.6667 7.8781 12.8334 7.00033 12.8334ZM7.00033 11.8334C8.4781 11.8334 9.73655 11.314 10.7757 10.2754C11.8143 9.23624 12.3337 7.9778 12.3337 6.50002C12.3337 5.02224 11.8143 3.7638 10.7757 2.72469C9.73655 1.68602 8.4781 1.16669 7.00033 1.16669C5.52255 1.16669 4.26433 1.68602 3.22566 2.72469C2.18655 3.7638 1.66699 5.02224 1.66699 6.50002C1.66699 7.9778 2.18655 9.23624 3.22566 10.2754C4.26433 11.314 5.52255 11.8334 7.00033 11.8334Z"
                          fill="white"
                        />
                      </svg>
                      {node?.attributes?.Date}
                    </div>
                    <div
                      onClick={() => {
                        handleSideBarClick(node?.attributes?.Title);
                      }}
                      role="button"
                      className="text-base font-bold cardClamp"
                    >
                      {node?.attributes?.Title}
                    </div>
                  </div>
                );
              })}
          </div>

          <div className="p-5 md:p-10 grow bg-white  bg-opacity-80 col-span-2 col-start-2 rounded-md shadow-md">
            <div className="text-black-75 text-xl md:text-2xl lg:text-3xl  my-7  font-custom  ">
              <div className="font-bold">Overview</div>
              {WikipeedikaBlogData[currentPage]?.attributes?.Overview.map(
                (node: any) => {
                  return (
                    <div
                      key={node?.Paragraph}
                      className="lg:text-lg text-base my-3"
                    >
                      {node?.Paragraph}
                    </div>
                  );
                }
              )}
            </div>
            <div className=" lg:text-lg text-base font-bold my-4">
              Additional Resources
            </div>
            <ul>
              {WikipeedikaBlogData[currentPage]?.attributes?.Links?.map(
                (node: any, index: number) => {
                  return (
                    <li key={node?.Link} className="list-decimal ml-5">
                      <a
                        href={node?.Link}
                        key={node?.Link}
                        className="lg:text-lg text-base my-3 block text-[#3F2305] underline "
                      >
                        <span className="">{node?.title}</span>
                      </a>
                    </li>
                  );
                }
              )}
            </ul>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default WikipeedikaIndividualBlogPage;
